import { useState, useCallback, useMemo, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { MaterialNames } from "@/components/material_symbol_names";
import ErrorBoundary from "@/components/ErrorBoundary";

import { renderPostStage } from "@/pages/_layouts/jobs/_utils";
import { useMutation, useQuery } from "@apollo/client";
import {
  JobPost,
  JobPostWrapper,
  PostStage,
  HiringStatus,
  ServiceLevel,
  JobActionType,
  JOB_ACTION_CONFIGS,
  canRevertToDraft,
  isExecuSearchServiceLevel
} from "@/pages/_layouts/jobs/types";
import {
  JOB_POST_DETAILS_QUERY,
  JOB_POST_WITH_INTERACTIONS_QUERY,
  SEND_REVIEW_REQUEST_MUTATION,
  PATCH_JOB_POST_MUTATION,
  DELETE_JOB_POST_MUTATION,
  JOB_POST_WITH_APPLICATIONS_QUERY
} from "./queries";
import { ObjectHeaderSkeleton } from "@/components/Skeleton";
import useCapabilities, { useCanAdminPublishJob, useHasRoles } from "@/hooks/useCapabilities";
import { PERMISSION } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import AppLayout from "@/layouts/AppLayout";
import type { Action } from "@/components/MenuButton";
import JobActionModal from "./_components/JobActionModal";
import SendJobPostReviewModal from "./_components/SendJobPostReviewModal";
import RenewJobPostModal from "./_components/RenewJobPostModal";
import { showSnackbar } from "@/lib/snackbarUtils";
import { getBackendNavigationOrigin } from "@/lib/backendSource";
import { isDev } from "@/lib/urlUtils";
import { createJobPath } from "@/pages/_layouts/jobs/_utils";
import Breadcrumb from "@/components/Breadcrumb";
import { useJobPostContext } from "./useJobPostContext";
import TabLayout from "./_JobPostIndexTabLayout";
import ObjectHeading from "@/components/ObjectHeading";
import QuickActionDialog from "./_components/QuickActionDialog";
import DownloadCSVDialog from "@/components/DownloadCSVDialog";
import { generateColumnData, columnHeaders as CSVHeaders } from "./_components/ApplicantReviewsExportButton/utils";

export interface JobsOutletContext {
  error: string;
  data: JobPostWrapper | undefined;
  loading: boolean;
  refetch: () => void;
  title: string;
  canManageJobs: boolean;
}

export interface JobPostDetailsLayoutProps {
  path: string;
  title: string;
}

/**
 * Gets navigation links for job-related pages
 */
const getJobNavLinksForLayout = () => [];

/**
 * Displays the details of a Job Post in a tabbed format.
 *
 * @param {Object} params - The input parameters.
 * @param {string} params.path - The root path. Used for back-navigation. Ex: /sections/:sectionId/jobs
 * @param {string} params.title - Refers to the location of the page. Used in page titles. Ex: Section, Facility
 */
export function JobPostIndexLayout({ path, title }: JobPostDetailsLayoutProps) {
  const { jobPostId } = useParams();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const basePath = paths.slice(0, paths.length - 1).join("/");
  const currentPath = paths[paths.length - 1];

  const navigate = useNavigate();
  const { hasPermission } = useCapabilities();
  const canAdminPublishJob = useCanAdminPublishJob();
  const { isJobClosable, isJobActive, isJobClosed, isJobInReviewStage } = useJobPostContext();
  const canManageJobs = hasPermission(PERMISSION.manageJobPosts);
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDownloadApplicantsDialog, setShowDownloadApplicantsDialog] = useState(false);
  const [sendReviewRequestOpen, setSendReviewRequestOpen] = useState(false);
  const [jobActionModalOpen, setJobActionModalOpen] = useState(false);
  const [jobActionInProgress, setJobActionInProgress] = useState(false);
  const [currentAction, setCurrentAction] = useState<JobActionType>("publish");
  const [isAdminPublish, setIsAdminPublish] = useState(false);
  const [renewJobPostModalOpen, setRenewJobPostModalOpen] = useState(false);
  const [renewJobPostInProgress, setRenewJobPostInProgress] = useState(false);
  const [jobPostReviewRequestVariables, setJobPostReviewRequestVariables] = useState<{ id: string; notes: string }>({
    id: "",
    notes: ""
  });

  // Determine if we're on interaction detail routes to conditionally fetch interactions
  const isInteractionsRoute = location.pathname.endsWith("/interactions");
  const isInteractionsDetailsRoute = location.pathname.includes("/interactions/");

  // Use interactions query for interaction detail pages, regular query otherwise
  const { data, loading, error, refetch } = useQuery<{ jobPost: JobPost }>(
    isInteractionsDetailsRoute ? JOB_POST_WITH_INTERACTIONS_QUERY : JOB_POST_DETAILS_QUERY,
    {
      variables: { jobPostId },
      skip: !jobPostId,
      fetchPolicy: "cache-and-network"
    }
  );

  // Use the same query as the applicants page so we can use cached data
  // Reviews are handled separately in the reviews tab, so we don't need the reviews query here
  const {
    data: applicationData,
    loading: isLoadingApplicants,
    error: applicantsError
  } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATIONS_QUERY, {
    variables: { jobPostId: jobPostId },
    skip: !jobPostId || !showDownloadApplicantsDialog,
    fetchPolicy: "cache-first"
  });

  // Mutations
  const [deleteJobPostMutation, { loading: deletingJobPost }] = useMutation(DELETE_JOB_POST_MUTATION);
  const [sendJobPostReviewRequestMutation, { loading: sendingReviewRequest }] =
    useMutation(SEND_REVIEW_REQUEST_MUTATION);
  const [patchJobPostMutation] = useMutation(PATCH_JOB_POST_MUTATION);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      error,
      data,
      loading,
      refetch,
      title,
      jobPostId,
      canManageJobs
    }),
    [error, data, loading, refetch, title, jobPostId, canManageJobs]
  );

  // Memoized tabs
  const tabs = useMemo(
    () => [
      ...(canManageJobs
        ? [
            {
              label: "Overview",
              path: createJobPath(jobPostId!, "details", basePath)
            },
            {
              label: "Applicants",
              path: createJobPath(jobPostId!, "applicants", basePath)
            },
            {
              label: "Interactions",
              path: createJobPath(jobPostId!, "interactions", basePath)
            },
            ...(data?.jobPost?.serviceLevel !== "Self-Serve" && data?.jobPost?.serviceLevel !== "Assisted"
              ? [
                  {
                    label: "Reviews",
                    path: createJobPath(jobPostId!, "reviews", basePath)
                  }
                ]
              : []),
            ...(data?.jobPost && isJobClosed(data.jobPost)
              ? [
                  {
                    label: "Outcome",
                    path: createJobPath(jobPostId!, "outcome", basePath)
                  }
                ]
              : [])
          ]
        : [])
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManageJobs, jobPostId, data?.jobPost?.serviceLevel, data?.jobPost, isJobClosed]
  );

  const actionButtons = useMemo<Action[]>(() => {
    if (!data) return [];

    const buttons: Action[] = [];

    if (canManageJobs) {
      buttons.push({
        text: "Edit Job",
        variant: "filled",
        onClick: () => navigate(`${path.split("/jobs")[0]}/jobs/job-post/edit/${jobPostId}`)
      });
    }

    if (isJobClosable(data.jobPost) && canManageJobs) {
      buttons.push({
        text: "Close Job",
        onClick: () => navigate(`${path.split("/jobs")[0]}/jobs/job-post/close/${jobPostId}`),
        testId: "close-job-button",
        section: "Manage"
      });
    }

    return buttons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, canManageJobs, isJobClosable, jobPostId, path]);

  const publishedPageAction = useMemo<Action | null>(() => {
    if (!data || isInteractionsRoute) return null;
    return {
      text: "Published Page",
      iconName: "open_in_new" as MaterialNames,
      onClick: () =>
        window.open(
          transformJobPostUrl(data.jobPost.pgaOrgUrl) || data.jobPost.externalJobUrl,
          "_blank",
          "noopener,noreferrer"
        ),
      testId: "view-published-page",
      section: "Manage"
    };
  }, [data, isInteractionsRoute]);

  const canPublishJob = (stage: PostStage, serviceLevel: ServiceLevel) => {
    if ([PostStage.PENDING, PostStage.ASSIST, PostStage.UNPOSTED, PostStage.REMOVED].includes(stage)) {
      return true;
    }
    if (stage === PostStage.DRAFT) {
      return [ServiceLevel.SELF_SERVE, ServiceLevel.ADVANCED, ServiceLevel.EXECUTIVE].includes(serviceLevel);
    }
    return false;
  };

  const extendedActions = useMemo<Action[]>(() => {
    if (!data || !canManageJobs) return [];

    const buttons: Action[] = [];

    if (data.jobPost.serviceLevel === ServiceLevel.ASSISTED && isJobInReviewStage(data.jobPost as JobPost)) {
      buttons.push({
        text: "Send for Review",
        onClick: () => setSendReviewRequestOpen(true),
        testId: "send-for-review-button",
        section: "Manage"
      });
    }

    if (canPublishJob(data.jobPost.stage as PostStage, data.jobPost.serviceLevel as ServiceLevel)) {
      buttons.push({
        text: "Publish Job",
        onClick: () => {
          setCurrentAction("publish");
          setIsAdminPublish(false);
          setJobActionModalOpen(true);
        },
        testId: "publish-job-button",
        section: "Manage"
      });
    }

    if (isJobActive(data.jobPost)) {
      buttons.push({
        text: "Unpublish Job",
        onClick: () => {
          setCurrentAction("unpublish");
          setJobActionModalOpen(true);
        },
        testId: "unpublish-job-button",
        section: "Manage"
      });
    }

    if (isJobActive(data.jobPost) && isExecuSearchServiceLevel(data.jobPost.serviceLevel)) {
      buttons.push({
        text: "Hide Job",
        onClick: () => {
          setCurrentAction("hide");
          setJobActionModalOpen(true);
        },
        testId: "hide-job-button",
        section: "Manage"
      });
    }

    if (canRevertToDraft(data.jobPost.stage, data.jobPost.status, data.jobPost.serviceLevel)) {
      buttons.push({
        text: "Revert to Draft",
        menuLabel: (
          <>
            Mark as <strong>Draft</strong>
          </>
        ),
        onClick: () => {
          setCurrentAction("revertToDraft");
          setJobActionModalOpen(true);
        },
        testId: "revert-to-draft-button",
        section: "Manage"
      });
    }

    if (
      (data.jobPost.stage === PostStage.ACTIVE || data.jobPost.stage === PostStage.INACTIVE) &&
      data.jobPost.status !== "Filled" &&
      data.jobPost.status !== "Incomplete"
    ) {
      buttons.push({
        text: "Renew Job Post",
        onClick: () => setRenewJobPostModalOpen(true),
        testId: "renew-job-post-button",
        section: "Manage"
      });
    }

    if (data.jobPost.stage === PostStage.IN_REVIEW && canAdminPublishJob) {
      buttons.push({
        text: "Admin Publish Job",
        onClick: () => {
          setCurrentAction("publish");
          setIsAdminPublish(true);
          setJobActionModalOpen(true);
        },
        testId: "admin-publish-job-button",
        section: "Manage"
      });
    }

    buttons.push({
      text: "Duplicate Job",
      onClick: () => navigate(`${path.split("/jobs")[0]}/jobs/job-post/create?duplicate=${jobPostId}`),
      testId: "duplicate-job-button",
      section: "Manage"
    });

    buttons.push({
      text: "Applicants",
      iconName: "download" as MaterialNames,
      onClick: () => setShowDownloadApplicantsDialog(true),
      testId: "download-applicants-menu-button",
      section: "Sharing"
    });

    buttons.push({
      text: "Delete Job",
      iconName: "delete" as MaterialNames,
      destructive: true,
      onClick: () => setShowDeleteDialog(true),
      testId: "delete-job-button"
    });

    if (isGlobalAdmin) {
      const backendOrigin = getBackendNavigationOrigin();
      buttons.push({
        text: "Rails Admin",
        iconName: "build" as MaterialNames,
        onClick: () =>
          window.open(`${backendOrigin}/pga_admin/jobs~job_post/${jobPostId}`, "_blank", "noopener,noreferrer"),
        testId: "rails-admin",
        section: "Admin/Dev Only"
      });
    }

    return buttons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, canManageJobs, jobPostId, path, isJobActive, canAdminPublishJob, isGlobalAdmin]);

  // Splice "Published Page" in as the last Manage action, before Sharing/destructive items.
  const composedActions = useMemo<Action[]>(() => {
    const merged = [...actionButtons, ...extendedActions];
    if (!publishedPageAction) return merged;

    const lastManageIdx = merged.map((a) => a.section).lastIndexOf("Manage");
    if (lastManageIdx === -1) return [...merged, publishedPageAction];
    return [...merged.slice(0, lastManageIdx + 1), publishedPageAction, ...merged.slice(lastManageIdx + 1)];
  }, [actionButtons, extendedActions, publishedPageAction]);

  // Set job post review request variables when data changes
  useEffect(() => {
    if (data?.jobPost?.id) {
      setJobPostReviewRequestVariables((variables) => ({
        ...variables,
        id: data.jobPost.id
      }));
    }
  }, [data?.jobPost?.id]);

  // Memoized header render function
  const renderHeader = useCallback(() => {
    if (loading && !data?.jobPost) return <ObjectHeaderSkeleton />;
    return data ? (
      <ObjectHeading
        title={data.jobPost.name || "Job"}
        iconName="business_center"
        isDetailsView
        metaData={[
          {
            text: data.jobPost.section?.name || "",
            boldValue: true
          },
          {
            label: "Facility",
            text: data.jobPost.facility?.name || ""
          },
          {
            label: "Consultant",
            text: data.jobPost.owner?.name || ""
          },
          {
            label: "Job Status",
            text: data.jobPost.status || ""
          }
        ]}
        tag={
          data.jobPost.reviewRequestSent
            ? renderPostStage(data.jobPost.stage, data.jobPost.reviewRequestSent as string, "strong")
            : renderPostStage(data.jobPost.stage, undefined, "strong")
        }
        actions={composedActions}
        showFullMetadataInCondensed={true}
      />
    ) : null;
  }, [loading, data, composedActions]);

  // Memoized handler functions
  const handleDeleteJobPost = useCallback(async () => {
    try {
      await deleteJobPostMutation({ variables: { jobPostId } });
      showSnackbar("Job post deleted successfully");
      navigate(`${path.split("/jobs")[0]}/jobs`);
    } catch (error) {
      showSnackbar("Error deleting job post");
    }
  }, [deleteJobPostMutation, jobPostId, path, navigate]);

  const sendJobPostReviewRequest = useCallback(async () => {
    try {
      const { data: requestData } = await sendJobPostReviewRequestMutation({
        variables: {
          input: jobPostReviewRequestVariables
        }
      });

      if (requestData?.sendJobPostReviewRequest?.success) {
        showSnackbar("Review request has been sent");
        setSendReviewRequestOpen(false);
        setJobPostReviewRequestVariables({
          ...jobPostReviewRequestVariables,
          notes: ""
        });
      } else {
        showSnackbar(
          requestData?.sendJobPostReviewRequest?.message ||
            "There was a problem sending this request for review. Please try again later."
        );
      }
    } catch {
      showSnackbar("There was a problem sending this request for review. Please try again later.");
    }
  }, [sendJobPostReviewRequestMutation, jobPostReviewRequestVariables]);

  const handleJobAction = useCallback(async () => {
    const config = JOB_ACTION_CONFIGS[currentAction];

    setJobActionInProgress(true);
    try {
      const input: {
        id: string;
        stage: string;
        termOfServiceAgreement?: boolean;
      } = {
        id: jobPostId!,
        stage: config.stage as PostStage
      };
      if (currentAction === "publish" && isAdminPublish) {
        input.termOfServiceAgreement = true;
      }

      const { data: patchData } = await patchJobPostMutation({
        variables: { input }
      });

      if (patchData?.patchJobPost?.success) {
        showSnackbar(config.successMessage);
        refetch();
      } else {
        showSnackbar(
          patchData?.patchJobPost?.message || `There was a problem ${config.errorAction}. Please try again later.`
        );
      }
    } catch (error) {
      showSnackbar(`There was a problem ${config.errorAction}. Please try again later.`);
    } finally {
      setJobActionInProgress(false);
      setJobActionModalOpen(false);
      setIsAdminPublish(false);
    }
  }, [currentAction, isAdminPublish, patchJobPostMutation, jobPostId, refetch]);

  const handleRenewJobPost = useCallback(
    async (updatedDeadline: string) => {
      setRenewJobPostInProgress(true);
      try {
        // full-cutover, explicitly sets stage, status, deadline, and publish time
        const { data: renewData } = await patchJobPostMutation({
          variables: {
            input: {
              id: jobPostId,
              stage: PostStage.ACTIVE,
              status: HiringStatus.IN_PROGRESS,
              submissionDeadline: updatedDeadline,
              jobPostPublishTime: new Date().toISOString()
            }
          }
        });

        if (renewData?.patchJobPost?.success) {
          showSnackbar("Job post has been renewed successfully");
          refetch();
          setRenewJobPostModalOpen(false);
        } else {
          showSnackbar(
            renewData?.patchJobPost?.message || "There was a problem renewing this job post. Please try again later."
          );
        }
      } catch (error) {
        showSnackbar("There was a problem renewing this job post. Please try again later.");
      } finally {
        setRenewJobPostInProgress(false);
      }
    },
    [patchJobPostMutation, jobPostId, refetch]
  );

  if (!jobPostId) {
    return (
      <div data-testid="error">
        <ErrorBoundary variant="inline" title="Invalid Page Access" message="Missing job post identifier" />
      </div>
    );
  }

  // Redirect to details tab by default if we're at the root job post URL
  if (currentPath == jobPostId || !currentPath) {
    return <Navigate to={`${path}/job-post/${jobPostId}/details`} replace />;
  }

  // If we're on the interaction details page, render just the Outlet without the layout
  if (isInteractionsDetailsRoute) {
    return <Outlet context={contextValue} />;
  }

  return (
    <AppLayout navLinks={getJobNavLinksForLayout()} interior bottomBar>
      <Breadcrumb compactOnly compactTitle="Job Details" backLink={{ to: "/jobs/all-jobs" }} />

      <div data-testid="job-post-page">
        {renderHeader()}
        <TabLayout jobPostId={jobPostId!} tabs={tabs}>
          <Outlet context={contextValue} />
        </TabLayout>
      </div>

      <JobActionModal
        open={jobActionModalOpen}
        setOpen={setJobActionModalOpen}
        handleAction={handleJobAction}
        disabled={jobActionInProgress}
        actionType={currentAction}
      />

      <SendJobPostReviewModal
        open={sendReviewRequestOpen}
        setOpen={setSendReviewRequestOpen}
        sendJobPostReviewRequest={sendJobPostReviewRequest}
        jobPostReviewRequestVariables={jobPostReviewRequestVariables}
        setJobPostReviewRequestVariables={setJobPostReviewRequestVariables}
        disabled={sendingReviewRequest}
      />

      <RenewJobPostModal
        open={renewJobPostModalOpen}
        setOpen={setRenewJobPostModalOpen}
        handleRenew={handleRenewJobPost}
        disabled={renewJobPostInProgress}
        currentSubmissionDeadline={data?.jobPost?.submissionDeadline as string}
      />

      <QuickActionDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        headline="Delete Job"
        message="Are you sure you want to delete this job? This action cannot be undone."
        actionButtonText="Delete"
        isLoading={deletingJobPost}
        actionButtonTestId="delete-job-button"
        onAction={handleDeleteJobPost}
        actionButtonTheme="error"
        actionButtonTrailingIcon={false}
        actionButtonIcon="delete"
      />

      <DownloadCSVDialog
        open={showDownloadApplicantsDialog}
        headline="Download Applicants"
        message="When your download is ready, click the button below to download your file. This may take a few moments."
        isLoading={isLoadingApplicants}
        error={applicantsError}
        data={applicationData?.jobPost?.applications || []}
        columnHeaders={CSVHeaders as string[]}
        fileTitle={`${data?.jobPost?.name?.trim().replace(/\s+/g, "-") || "Job-Position"}-${
          data?.jobPost?.facility?.name?.trim().replace(/\s+/g, "-") || "Unknown-Facility"
        }`}
        generateColumnData={generateColumnData}
        actionButtonTestId="download-applicants-button"
        onClose={() => {
          setShowDownloadApplicantsDialog(false);
        }}
      />
    </AppLayout>
  );
}

/**
 * Transforms job post URLs based on the current environment.
 * In development or non-production environments, it will redirect to the dev domain
 * and ensure the URL contains "sandbox" in the domain.
 */
function transformJobPostUrl(url: string | null): string {
  if (!url) return "";

  // In dev environment, transform jobs.pga.org to jobs.sandboxpga.org
  if (isDev() && !url.includes("sandbox")) {
    url = url.replace(/(\/\/jobs\.)(pga\.org)/, "$1sandbox$2");
  }

  return url;
}
